import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectOption } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/toast';
import { Upload, Plus, ChevronLeft, ChevronRight, Zap, Trash2 } from 'lucide-react';
import api from '../services/api';

const statusColors = {
  pending: 'secondary', out_for_delivery: 'info', postponed: 'warning',
  cancelled: 'destructive', delivered: 'success',
  tomorrow: 'warning', phone_not_answered: 'destructive', cannot_contact: 'destructive',
  wrong_phone: 'destructive', unclear_address: 'destructive',
  gate_locked: 'warning', entrance_chip: 'warning', other: 'secondary', no_one_home: 'destructive',
  returned: 'destructive',
};
const statusLabels = {
  pending: 'Хүлээгдэж буй', out_for_delivery: 'Хүргэлтэнд', postponed: 'Хойшлуулсан',
  cancelled: 'Цуцалсан', delivered: 'Хүргэгдсэн',
  tomorrow: 'Маргааш авах', phone_not_answered: 'Утас авахгүй', cannot_contact: 'Холбогдохгүй',
  wrong_phone: 'Утасны дугаар буруу', unclear_address: 'Хаяг тодорхойгүй',
  gate_locked: 'Хотхоны хаалт', entrance_chip: 'Орц байрны чип', other: 'Бусад', no_one_home: 'Хаяг хүнгүй',
  returned: 'Буцаасан',
};
const filterStatusLabels = {
  pending: 'Хүлээгдэж буй', out_for_delivery: 'Хүргэлтэнд', postponed: 'Хойшлуулсан',
  cancelled: 'Цуцалсан', delivered: 'Хүргэгдсэн',
  tomorrow: 'Маргааш авах', phone_not_answered: 'Утас авахгүй', cannot_contact: 'Холбогдохгүй',
  wrong_phone: 'Утасны дугаар буруу', unclear_address: 'Хаяг тодорхойгүй',
  gate_locked: 'Хотхоны хаалт', entrance_chip: 'Орц байрны чип', other: 'Бусад', no_one_home: 'Хаяг хүнгүй',
  returned: 'Буцаасан',
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [assignModal, setAssignModal] = useState({ open: false, orderIds: [] });
  const [selectedRows, setSelectedRows] = useState([]);
  const [errorsModal, setErrorsModal] = useState({ open: false, errors: [] });
  const [dispatchModal, setDispatchModal] = useState({ open: false, file: null });
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);
  const [form, setForm] = useState({});
  const [editingOrder, setEditingOrder] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const fetchOrders = async (p = page, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50, ...f };
      const res = await api.get('/orders', { params });
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch {
      toast({ title: 'Захиалга татахад алдаа гарлаа', variant: 'error' });
    }
    setLoading(false);
  };

  const fetchDistricts = async () => {
    try { const res = await api.get('/districts'); setDistricts(res.data); } catch {}
  };
  const fetchDrivers = async () => {
    try { const res = await api.get('/drivers'); setDrivers(res.data); } catch {}
  };

  useEffect(() => { fetchOrders(); fetchDistricts(); fetchDrivers(); }, []);

  const uploadFile = async (file, dispatchedAt) => {
    const formData = new FormData();
    formData.append('file', file);
    if (dispatchedAt) formData.append('dispatchedAt', dispatchedAt.toISOString());
    try {
      const res = await api.post('/orders/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: res.data.message, variant: 'success' });
      if (res.data.errors?.length > 0) {
        setErrorsModal({ open: true, errors: res.data.errors });
      }
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Excel upload алдаа', variant: 'error' });
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // UB +08:00 цагаар Баасан өдөр (5) бол админаас сонголт авна
    const nowUB = new Date(Date.now() + 8 * 60 * 60 * 1000);
    const isFriday = nowUB.getUTCDay() === 5;
    if (isFriday) {
      setDispatchModal({ open: true, file });
      return;
    }
    await uploadFile(file, null);
  };

  const handleDispatchChoice = async (choice) => {
    const { file } = dispatchModal;
    setDispatchModal({ open: false, file: null });
    if (!file) return;
    // UB midnight today (UTC = UB - 8h)
    const nowMs = Date.now();
    const ub = new Date(nowMs + 8 * 60 * 60 * 1000);
    const ubY = ub.getUTCFullYear();
    const ubM = ub.getUTCMonth();
    const ubD = ub.getUTCDate();
    let dispatchedAt;
    if (choice === 'today1830') {
      // Тухайн өдрийн 18:30 UB
      dispatchedAt = new Date(Date.UTC(ubY, ubM, ubD, 18, 30) - 8 * 60 * 60 * 1000);
    } else {
      // Маргаашийн 09:30 UB
      dispatchedAt = new Date(Date.UTC(ubY, ubM, ubD + 1, 9, 30) - 8 * 60 * 60 * 1000);
    }
    await uploadFile(file, dispatchedAt);
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm('Энэ захиалгыг устгах уу?')) return;
    try {
      await api.delete(`/orders/${id}`);
      toast({ title: 'Захиалга устгагдлаа', variant: 'success' });
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Устгах алдаа', variant: 'error' });
    }
  };

  const handleReturnOrder = async (id) => {
    if (!window.confirm('Энэ захиалгыг буцаах уу? (ачаа буруу)')) return;
    try {
      await api.put(`/orders/${id}`, { status: 'returned' });
      toast({ title: 'Захиалга буцаагдлаа', variant: 'success' });
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Өнөөдрийн захиалгыг устгах уу?')) return;
    try {
      const res = await api.delete('/orders');
      toast({ title: res.data.message, variant: 'success' });
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Устгах алдаа', variant: 'error' });
    }
  };

  const handleSaveOrder = async () => {
    try {
      const data = { ...form };
      if (data.boxesStr !== undefined) {
        data.boxes = data.boxesStr ? data.boxesStr.split(',').map(s => s.trim()).filter(Boolean) : [];
        delete data.boxesStr;
      }
      if (data.weightsStr !== undefined) {
        data.weights = data.weightsStr ? data.weightsStr.split(',').map(s => s.trim()).filter(Boolean) : [];
        delete data.weightsStr;
      }
      if (editingOrder) {
        await api.put(`/orders/${editingOrder._id}`, data);
        toast({ title: 'Захиалга шинэчлэгдлээ', variant: 'success' });
      } else {
        await api.post('/orders', data);
        toast({ title: 'Захиалга үүсгэгдлээ', variant: 'success' });
      }
      setModalOpen(false);
      setForm({});
      setEditingOrder(null);
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const openEditOrder = (order) => {
    setEditingOrder(order);
    setForm({
      orderNumber: order.orderNumber || '',
      receiverName: order.receiverName || '',
      phone1: order.phone1 || '',
      phone2: order.phone2 || '',
      district: order.district?._id || '',
      khoroo: order.khoroo || '',
      address: order.address || '',
      boxesStr: order.boxes?.join(', ') || '',
      weightsStr: order.weights?.join(', ') || '',
    });
    setModalOpen(true);
  };

  const handleAssign = async (driverId) => {
    try {
      if (assignModal.orderIds.length === 1) {
        await api.post('/deliveries/assign', { orderId: assignModal.orderIds[0], driverId });
      } else {
        await api.post('/deliveries/assign-bulk', { orderIds: assignModal.orderIds, driverId });
      }
      toast({ title: 'Хүргэлтийн ажилтанд хуваарилагдлаа', variant: 'success' });
      setAssignModal({ open: false, orderIds: [] });
      setSelectedRows([]);
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const handleAutoAssign = async () => {
    setAutoAssignLoading(true);
    try {
      // Бүх pending захиалгуудын ID-г backend-ээс авах
      const pendingRes = await api.get('/orders', { params: { status: 'pending', limit: 10000 } });
      const pendingIds = pendingRes.data.orders.map(o => o._id);
      if (pendingIds.length === 0) {
        toast({ title: 'Хуваарилах захиалга байхгүй', variant: 'error' });
        setAutoAssignLoading(false);
        return;
      }
      const res = await api.post('/deliveries/auto-assign', { orderIds: pendingIds });
      toast({ title: `${res.data.assigned} захиалга автомат хуваарилагдлаа`, variant: 'success' });
      if (res.data.errors?.length > 0) {
        setErrorsModal({ open: true, errors: res.data.errors.map(e => ({ row: '-', message: e.message })) });
      }
      setSelectedRows([]);
      fetchOrders();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Автомат хуваарилалт алдаа', variant: 'error' });
    }
    setAutoAssignLoading(false);
  };

  const toggleRow = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const pendingIds = orders.filter(o => o.status === 'pending').map(o => o._id);
    if (selectedRows.length === pendingIds.length) setSelectedRows([]);
    else setSelectedRows(pendingIds);
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold">Захиалгууд ({total})</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={filters.district || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, district: v }));
            fetchOrders(1, { ...filters, district: v });
          }} className="w-36">
            <SelectOption value="">Дүүрэг</SelectOption>
            {districts.map(d => <SelectOption key={d._id} value={d._id}>{d.name}</SelectOption>)}
          </Select>
          <Select value={filters.status || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, status: v }));
            fetchOrders(1, { ...filters, status: v });
          }} className="w-40">
            <SelectOption value="">Төлөв</SelectOption>
            {Object.entries(filterStatusLabels).map(([k, v]) => <SelectOption key={k} value={k}>{v}</SelectOption>)}
          </Select>
          {selectedRows.length > 0 && (
            <Button onClick={() => setAssignModal({ open: true, orderIds: selectedRows })}>
              {selectedRows.length} хуваарилах
            </Button>
          )}
          <Button variant="outline" className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={handleAutoAssign} disabled={autoAssignLoading}>
            {autoAssignLoading ? <Spinner className="h-4 w-4 mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
            {autoAssignLoading ? 'Хуваарилж байна...' : 'Автомат хуваарилах'}
          </Button>
          <input type="file" ref={fileInputRef} onChange={handleUpload} accept=".xlsx,.xls" className="hidden" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />Excel
          </Button>
          <Button variant="outline" className="bg-red-50 border-red-300 text-red-700 hover:bg-red-100" onClick={handleDeleteAll}>
            <Trash2 className="h-4 w-4 mr-2" />Өнөөдрийнхийг устгах
          </Button>
          <Button onClick={() => { setForm({}); setEditingOrder(null); setModalOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />Нэмэх
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" checked={selectedRows.length > 0 && selectedRows.length === orders.filter(o => o.status === 'pending').length}
                      onChange={toggleAll} className="rounded" />
                  </TableHead>
                  <TableHead>Дугаар</TableHead>
                  <TableHead>Хүлээн авагч</TableHead>
                  <TableHead>Утас</TableHead>
                  <TableHead>Дүүрэг</TableHead>
                  <TableHead>Хороо</TableHead>
                  <TableHead>Хаяг</TableHead>
                  <TableHead>Ачаа</TableHead>
                  <TableHead>Бэлт. огноо</TableHead>
                  <TableHead>Төлөв</TableHead>
                  <TableHead>Үйлдэл</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => (
                  <TableRow key={order._id}>
                    <TableCell>
                      <input type="checkbox" disabled={order.status !== 'pending'}
                        checked={selectedRows.includes(order._id)}
                        onChange={() => toggleRow(order._id)} className="rounded" />
                    </TableCell>
                    <TableCell className="font-medium">{order.orderNumber}</TableCell>
                    <TableCell>{order.receiverName}</TableCell>
                    <TableCell>{order.phone1}</TableCell>
                    <TableCell>{order.district?.name}</TableCell>
                    <TableCell>{order.khoroo ? `${order.khoroo}-р` : '-'}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{order.address}</TableCell>
                    <TableCell className="text-xs">
                      {order.boxes?.length > 0
                        ? order.boxes.map((b, i) => `${b}${order.weights?.[i] ? ' - ' + order.weights[i] + 'гр' : ''}`).join(', ')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-xs">{order.readyDate ? new Date(order.readyDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell><Badge variant={statusColors[order.status]}>{statusLabels[order.status]}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEditOrder(order)}>Засах</Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteOrder(order._id)}>Устгах</Button>
                        {!['returned', 'delivered', 'cancelled'].includes(order.status) && (
                          <Button size="sm" variant="outline" className="text-orange-600 hover:bg-orange-50" onClick={() => handleReturnOrder(order._id)}>Буцаах</Button>
                        )}
                        {order.status === 'pending' && (
                          <Button size="sm" onClick={() => setAssignModal({ open: true, orderIds: [order._id] })}>
                            Хуваарилах
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-zinc-400">Захиалга байхгүй</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchOrders(page - 1); }}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-zinc-600">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(page + 1); fetchOrders(page + 1); }}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create order modal */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) { setModalOpen(false); setEditingOrder(null); setForm({}); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingOrder ? 'Захиалга засах' : 'Захиалга нэмэх'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Дугаар *</label>
              <Input value={form.orderNumber || ''} onChange={e => setForm(f => ({ ...f, orderNumber: e.target.value }))} /></div>
            <div><label className="text-sm font-medium">Хүлээн авагч *</label>
              <Input value={form.receiverName || ''} onChange={e => setForm(f => ({ ...f, receiverName: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Утас *</label>
                <Input value={form.phone1 || ''} onChange={e => setForm(f => ({ ...f, phone1: e.target.value }))} /></div>
              <div><label className="text-sm font-medium">Утас 2</label>
                <Input value={form.phone2 || ''} onChange={e => setForm(f => ({ ...f, phone2: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Дүүрэг *</label>
                <Select value={form.district || ''} onChange={e => setForm(f => ({ ...f, district: e.target.value, khoroo: '' }))}>
                  <SelectOption value="">Сонгох</SelectOption>
                  {districts.map(d => <SelectOption key={d._id} value={d._id}>{d.name}</SelectOption>)}
                </Select></div>
              <div><label className="text-sm font-medium">Хороо</label>
                <Select value={form.khoroo || ''} onChange={e => setForm(f => ({ ...f, khoroo: e.target.value ? Number(e.target.value) : '' }))}
                  disabled={!form.district}>
                  <SelectOption value="">Сонгох</SelectOption>
                  {(districts.find(d => d._id === form.district)?.khoroos || []).map(k => (
                    <SelectOption key={k} value={k}>{k}-р хороо</SelectOption>
                  ))}
                </Select></div>
            </div>
            <div><label className="text-sm font-medium">Хаяг *</label>
              <Input value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">BOX (таслалаар)</label>
                <Input placeholder="1, 2, 3" value={form.boxesStr || ''} onChange={e => setForm(f => ({ ...f, boxesStr: e.target.value }))} /></div>
              <div><label className="text-sm font-medium">Жин (таслалаар)</label>
                <Input placeholder="1.5, 2" value={form.weightsStr || ''} onChange={e => setForm(f => ({ ...f, weightsStr: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setModalOpen(false); setEditingOrder(null); setForm({}); }}>Болих</Button>
            <Button onClick={handleSaveOrder}>{editingOrder ? 'Хадгалах' : 'Үүсгэх'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign modal */}
      <Dialog open={assignModal.open} onOpenChange={(v) => !v && setAssignModal({ open: false, orderIds: [] })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Хүргэлтийн ажилтанд хуваарилах</DialogTitle>
            <p className="text-sm text-zinc-500">{assignModal.orderIds.length} захиалга сонгогдсон</p>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {drivers.filter(d => d.isActive).map(d => (
              <button key={d._id} className="w-full text-left p-3 rounded-lg border hover:bg-zinc-50 transition-colors"
                onClick={() => handleAssign(d._id)}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{d.name}</span>
                  <span className="text-xs text-zinc-400">{d.phone}</span>
                </div>
                {d.districts?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {d.districts.map(dist => (
                      <span key={dist._id} className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">
                        {dist.name}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispatched date choice modal (Friday only) */}
      <Dialog open={dispatchModal.open} onOpenChange={(v) => !v && setDispatchModal({ open: false, file: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dispatched огноо сонгоно уу</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-600 mb-4">
            Өнөөдөр Баасан гариг. Хүргэлтийн dispatched огноо хэзээ байх вэ?
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDispatchChoice('today1830')}>
              Өнөөдөр 18:30
            </Button>
            <Button onClick={() => handleDispatchChoice('tomorrow0930')}>
              Маргааш 09:30
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Errors modal */}
      <Dialog open={errorsModal.open} onOpenChange={(v) => !v && setErrorsModal({ open: false, errors: [] })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Зарим мөрөнд алдаа гарлаа</DialogTitle></DialogHeader>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {errorsModal.errors.map((e, i) => (
              <div key={i} className="text-sm text-red-600">Мөр {e.row}: {e.message}</div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
